import { IVatSummary } from '../@types/finance';

/**
 * CSV export of the VAT & Partial Exemption tab for one quarter.
 *
 * Every line carries the period it covers (from / to / quarter), so a row stays
 * meaningful once it is pasted into another sheet or filtered on its own.
 * Values are plain numbers with two decimals — no currency symbol or thousands
 * separators — so a spreadsheet reads them as numbers.
 */

const HEADER = [
  'Period from',
  'Period to',
  'Quarter',
  'Section',
  'Box / code',
  'Description',
  'Value',
];

type Period = { dateFrom: string; dateTo: string; quarterLabel: string };

/** Quote a cell when it holds a delimiter, quote or line break. */
function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function amount(value: number | null | undefined): string {
  return Number(value ?? 0).toFixed(2);
}

export function buildVatCsv(summary: IVatSummary, period: Period): string {
  const rows: (string | number)[][] = [];
  const add = (section: string, box: string, label: string, value: string) =>
    rows.push([
      period.dateFrom,
      period.dateTo,
      period.quarterLabel,
      section,
      box,
      label,
      value,
    ]);

  const pe = 'Partial exemption';
  add(
    pe,
    '',
    'Taxable turnover (S + Z, net of apportionment)',
    amount(summary.taxableTurnover),
  );
  add(
    pe,
    '',
    'Exempt turnover (E + dispensing service)',
    amount(summary.exemptTurnover),
  );
  add(pe, '', 'NHS / no-VAT income (N coded)', amount(summary.nhsTurnover));
  add(pe, '', 'Total turnover', amount(summary.totalTurnover));
  add(
    pe,
    '',
    'Fully deductible input VAT (attributable to taxable)',
    amount(summary.fullyDeductibleInputVat),
  );
  add(
    pe,
    '',
    'Input VAT attributable to exempt (not recoverable)',
    amount(summary.attributableExemptInputVat),
  );
  add(
    pe,
    '',
    'Residual input VAT (mixed use)',
    amount(summary.residualInputVat),
  );
  add(
    pe,
    '',
    'PE recovery rate (%)',
    ((summary.peRecoveryRate ?? 0) * 100).toFixed(1),
  );
  add(pe, '', 'Residual VAT recovered', amount(summary.recoverableResidualVat));
  add(
    pe,
    '',
    'Total recoverable input VAT',
    amount(summary.totalRecoverableVat),
  );
  add(pe, '', 'Irrecoverable input VAT', amount(summary.irrecoverableVat));
  if (summary.deMinimis) {
    add(
      pe,
      '',
      'De minimis test',
      summary.deMinimis.passed ? 'PASSED' : 'FAILED',
    );
    add(
      pe,
      '',
      'Exempt input tax for the period',
      amount(summary.deMinimis.exemptInputTax),
    );
  }

  for (const item of summary.breakdown ?? []) {
    add(
      'Turnover by VAT code',
      item.code,
      'Turnover (ex. VAT)',
      amount(item.totalValue),
    );
    add('Turnover by VAT code', item.code, 'VAT', amount(item.vatAmount));
    add(
      'Turnover by VAT code',
      item.code,
      'Transactions',
      String(item.transactionCount),
    );
  }

  const v = summary.vat100;
  const box = 'VAT100 return';
  add(
    box,
    '1',
    'VAT due on sales and other outputs (post-apportionment)',
    amount(v?.box1OutputTax),
  );
  add(box, '2', 'VAT due on EC acquisitions', amount(v?.box2EcAcquisitions));
  add(box, '3', 'Total VAT due (Box 1 + Box 2)', amount(v?.box3TotalVatDue));
  add(
    box,
    '4',
    'VAT reclaimed on purchases (input tax)',
    amount(v?.box4InputTax),
  );
  add(
    box,
    '5',
    'Net VAT payable to HMRC (Box 3 - Box 4; negative = reclaimable)',
    amount(v?.box5NetVatDue),
  );
  add(
    box,
    '6',
    'Total value of sales and other outputs (ex. VAT)',
    amount(v?.box6TotalSalesExVat),
  );
  add(
    box,
    '7',
    'Total value of purchases and other inputs (ex. VAT)',
    amount(v?.box7TotalPurchasesExVat),
  );
  add(box, '8', 'Total value of EC sales', amount(v?.box8EcSales));
  add(
    box,
    '9',
    'Total value of EC acquisitions',
    amount(v?.box9EcAcquisitions),
  );

  return [HEADER, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
}
